import { tv } from 'tailwind-variants'

const AVATAR_COLORS = [
  'bg-generic-soft-mint-green',
  'bg-generic-light-sky-blue',
  'bg-generic-light-orange',
  'bg-generic-pastel-pink',
  'bg-generic-pastel-peach',
  'bg-generic-pastel-yellow',
  'bg-generic-pastel-green',
  'bg-generic-muted-sage-green',
  'bg-generic-warm-yellow',
  'bg-primary-50',
  'bg-secondary-100',
  'bg-success-50',
  'bg-information-50'
] as const

export interface TAvatarProps {
  name: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  colorMode?: 'random' | 'consistent'
  className?: string
}

export const avatarVariants = tv({
  slots: {
    containerSlot:
      'font-body text-surface-black-50 flex items-center justify-center rounded-full font-bold leading-[150%] tracking-normal',
    initialsSlot: ''
  },
  variants: {
    size: {
      sm: {
        containerSlot: 'text-caption-sm h-6 w-6'
      },
      md: {
        containerSlot: 'text-caption-md h-8 w-8'
      },
      lg: {
        containerSlot: 'text-body-md h-10 w-10'
      },
      xl: {
        containerSlot: 'text-body-lg h-12 w-12'
      }
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

export const getInitials = (name: string): string => {
  if (!name) return ''

  return name
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const getRandomAvatarColor = (): string =>
  AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)]

export const getConsistentAvatarColor = (name: string): string => {
  if (!name) return AVATAR_COLORS[0]

  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

export const Avatar = ({
  name,
  size = 'md',
  colorMode = 'random',
  className
}: TAvatarProps): JSX.Element => {
  const { containerSlot } = avatarVariants({ size })
  const initials = getInitials(name)
  const bgColor =
    colorMode === 'random'
      ? getRandomAvatarColor()
      : getConsistentAvatarColor(name)

  return (
    <div
      className={containerSlot({ className: `${bgColor} ${className || ''}` })}
    >
      {initials}
    </div>
  )
}
